import React, { useState } from "react";
import { Sparkles, X, Coins, AlertCircle, ShieldCheck } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { toast } from "sonner";
import api from "../../lib/apiClient";
import { useAuth } from "../../context/AuthContext";

const LISTING_FEE = 1;
const MIN_OFFER = 10;
const MAX_OFFER = 10000;
const PLATFORM_PCT = 5;

// MakeOfferModal — anonymous purchase offer on a single video.
// Caller renders <MakeOfferModal videoId={...} onClose={...} />.
//
// The seller (current owner) will only ever see the AMOUNT and the
// expiry. The offerer's identity is stored server-side but never
// returned in /offers/received. This is the "no risk to bid" psychology
// the user designed: producers stay aspirational because they can't
// scoff at any specific person's offer — they just see the number.
const MakeOfferModal = ({ videoId, videoTitle, onClose, onSent }) => {
  const { user, refreshUser } = useAuth();
  const [amount, setAmount] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const parsed = parseFloat(amount);
  const valid = !Number.isNaN(parsed) && parsed >= MIN_OFFER && parsed <= MAX_OFFER;
  const totalCharge = valid ? parsed + LISTING_FEE : 0;
  const insufficient = valid && user && user.credits < totalCharge;

  const submit = async () => {
    setError("");
    if (!valid) return;
    if (insufficient) {
      setError(`You need ${totalCharge.toFixed(0)} cr (${LISTING_FEE} cr fee + ${parsed.toFixed(0)} cr hold). You have ${user.credits.toFixed(2)}.`);
      return;
    }
    setSending(true);
    try {
      await api.post(
        `/marketplace/videos/${videoId}/offers`,
        { amount_credits: parsed },
      );
      toast.success("Offer sent anonymously. They have 24 h to respond.");
      await refreshUser();
      if (onSent) onSent();
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't submit offer";
      setError(msg);
    }
    setSending(false);
  };

  if (!user) return null;

  return (
    <div
      className="fixed inset-0 z-[9100] flex items-center justify-center bg-black/75 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="make-offer-modal"
    >
      <div
        className="relative w-full max-w-md bg-gray-900 border border-amber-500/30 rounded-2xl shadow-2xl p-5 sm:p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 bg-black/60 hover:bg-black/80 text-white p-1.5 rounded-full transition-colors"
          data-testid="make-offer-close-btn"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="flex items-center gap-2 text-amber-300 text-[11px] uppercase tracking-widest mb-2.5">
          <Sparkles className="h-4 w-4" /> Anonymous offer
        </div>
        <h2 className="text-white text-xl font-semibold leading-tight mb-1">
          Make an offer on this video
        </h2>
        <p className="text-gray-400 text-sm leading-relaxed mb-4 truncate">
          "{videoTitle || 'Untitled'}"
        </p>

        <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-3 flex items-start gap-2.5 mb-4">
          <ShieldCheck className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" />
          <div className="text-xs text-gray-300 leading-relaxed">
            <span className="text-emerald-400 font-medium">100% anonymous.</span>{" "}
            The owner only sees the amount and that an offer arrived — never your name. Bid freely.
          </div>
        </div>

        <label className="block text-gray-400 text-xs uppercase tracking-wider mb-1">
          Offer amount (credits)
        </label>
        <div className="relative mb-2">
          <Coins className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-amber-300" />
          <Input
            type="number"
            inputMode="numeric"
            min={MIN_OFFER}
            max={MAX_OFFER}
            placeholder={`${MIN_OFFER}–${MAX_OFFER}`}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="pl-9 bg-gray-800 border-gray-700 text-white"
            data-testid="make-offer-amount-input"
            autoFocus
          />
        </div>

        <ul className="text-[11px] text-gray-500 space-y-0.5 mb-4">
          <li>• Listing fee: <span className="text-amber-300">{LISTING_FEE} cr non-refundable</span> (oops refund if you cancel within 1 h)</li>
          <li>• 24-hour expiry — auto-refunded if they don't respond</li>
          <li>• Platform takes {PLATFORM_PCT}% on accept</li>
          <li>• Min {MIN_OFFER} cr · Max {MAX_OFFER.toLocaleString()} cr · Max 20 offers/day</li>
        </ul>

        {valid && (
          <div className="bg-gray-800/60 rounded p-2.5 text-xs text-gray-300 mb-3">
            You'll be charged{" "}
            <span className="text-white font-semibold">
              {totalCharge.toLocaleString()} cr
            </span>{" "}
            ({LISTING_FEE} cr fee + {parsed.toLocaleString()} cr held in escrow)
          </div>
        )}

        {error && (
          <div className="flex items-start gap-2 bg-red-500/10 border border-red-500/30 rounded p-2.5 text-xs text-red-300 mb-3" data-testid="make-offer-error">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" /> {error}
          </div>
        )}

        <Button
          onClick={submit}
          disabled={!valid || sending || insufficient}
          className="w-full bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-gray-900 font-bold h-11"
          data-testid="make-offer-submit-btn"
        >
          {sending ? "Sending…" : `Send anonymous offer${valid ? ` (${totalCharge.toLocaleString()} cr)` : ""}`}
        </Button>
      </div>
    </div>
  );
};

export default MakeOfferModal;
